'use strict';

const EventEmitter = require('events');

import BuiltinRepository from './repository/BuiltinRepository';
import FacetRepository from './repository/FacetRepository';
import GoogleMap from './map/GoogleMap';
import OpenLayerMap from './map/OpenLayerMap';
import PopupFactory from './PopupFactory';
import Controller from './Controller';
import PlaceType from './PlaceType';
import Request from './Request';

// Modules
import IHGoogleAnalytics from './modules/IHGoogleAnalytics.js';
import IHKeywordSearch from './modules/IHKeywordSearch';

/**
 * @name IHOptions
 * @type {Object}
 * @property {GoogleMapOptions|OpenLayerMapOptions} mapOptions
 * @property {BuiltinRepositoryOptions|FacetRepositoryOptions} repositoryOptions
 * 
 */

/**
 * @class IH
 *
 * events:
 *   ihClickMarker      マーカークリック時
 *   ihUpdateCenterMarker 基準位置移動時
 *   ihReady (or ready) start()完了後
 *   ihClear            clear()実行後
 *   ihBeforeRequestPlaces requestPlaces()で検索リクエスト発生前
 *   ihAfterTransformPlacesResponse レスポンスから施設オブジェクト生成後
 *   ihAfterRequestPlaces  施設オブジェクト拡張後
 *   ihFillMarkerPopup  マーカーのInfoWindowのDOM構築後
 *   ihFillSearchResult 結果リストの１項目のDOM構築後
 *   
 */
class IH {
  /**
   * 
   * @access public
   * @param {IHOptions} options
   * @returns void
   */
  constructor(options) {
    
    var vars = this.getProperties();
    for (var i in vars) {
      this[i] = vars[i];
    }
    
    if (console) {
      console.log(options);
    }
    
    // モジュールを読み込む
    if (options.modules) {
      this.modules = options.modules;
      delete options.modules;
    }
    // モジュールを読み込む（modulesを上書きせずに差分のみ指定したい場合）
    if (options.modules_append) {
      this.modules = this.modules.concat(options.modules_append);
      delete options.modules_append;
    }
    
    if (options.locale) {
      this.locale = options.locale;
    }
    if (options.translationsCommon) {
      this.translationsCommon = options.translationsCommon;
    }
    if (options.translationsExtend) {
      this.translationsExtend = options.translationsExtend;
    }
    
    this.extendModules(options);

    // モジュールを読み込んだ後に、変数を設定する
    // モジュールの初期値を上書きできるようにするため
    this.setOptions(options);

    // マップフレームワークに GoogleMapsAPI を指定してもAPIに接続できない場合は OpenLayers に切り替える
    if (typeof google === 'undefined') {
      this.mapOptions.type = 'openlayer';
    }
  
    this.controller = this.createController(options);
    this.controller.apply(this);
  }
  /**
   * 
   * @returns {Controller}
   */
  createController(options) {
    return new Controller(options);
  }
  /**
   * インスタンス変数
   * 
   */
  getProperties() {
    return {
      /**
       * @type {boolean}
       */
      debug: false,
      /**
       * @vars {{lat: Number, lng: Number}}
       */
      init_location: {lat: 34.846142, lng: 135.617251},
      /**
       * 施設情報。initializeでHTML(#facility_list)の情報を読み取る
       */
      flag_display_facility: [], // [0]: 緊急避難所
      /**
       * マーカーの画像パス
       * @type {string}
       */
      icon_file_path: 'img/',
      /**
       * 施設画像のパス
       */
      place_image_path: '',
      /**
       * デフォルトのマーカー画像
       */
      icon_default: 'img/default_min.png',
      /**
       * 選択中の半径
       * @access protected
       * @type String
       */
      selected_radius: "0.5",
      /**
       * @type {IHModule[]}
       * ・IHGeolocation（オンライン限定？）
       * ・IHGoogleAnalytics（オンライン限定）
       * ・IHDirections（GoogleMapsAPI & オンライン限定）
       * ・IHElevation（GoogleMapsAPI & オンライン限定）
       * ・IHKeywordSearch
       */
      modules: [
        IHGoogleAnalytics,
        IHKeywordSearch
      ],
      lastClickedPlace: null,
      /**
       * 検索時に基準位置を移動させるかどうか
       * @type Boolean
       */
      updateBaseMarker: false,
      /**
       * マーカークリック時にパネルを表示するかどうか
       */
      show_detail_panel: null,
      /**
       * 施設タイプの定義
       * @type {[type]}
       */
      placeTypes: null,
      /**
       * サービス
       */
      services: {},
      /**
       * サービスプロバイダ
       */
      serviceProvider: {
        map: typeof google === 'undefined' ? {} : {
          Elevation: google.maps.ElevationService,
          Direction: google.maps.DirectionsService
        },
        get: function(name) {
          return new this.map[name]();
        }
      },
      searchResultOffset: 0,
      searchResultLimit: 30,
      searchResultPlaces: null,
      repositoryOptions: {
        type: 'builtin',  // or 'facet'
        placeTypeFacetName: '種別',
        searchApi: 'search',
        mappingItems: {
          lat: 'lat',
          lng: 'lng',
          image: 'image',
          image_label: 'image_label',
          name: 'name',
          address: 'address',
          description: 'description',
          info: 'info'
        },
        sortTarget: {}, // ex. kana: 'よみがな', era: '時代'
        sortValue: {},  // ex. eras: { '縄文': 1, '弥生':2, ... '不明': 100 },
        firstSelection: '',
        multiSortValues: false,  // ※ファセット値に半角スペースは使用不可
        // ファセット内階層有効時に子ファセット値をPlaceTypeに指定する場合は true にして親ファセット値を除去する（「親 > 子」となるため）
        // ただし、空白が除去されることで不具合が発生する可能性あり（TODO: この点を改善したい）
        enableDividePlaceTypeBySpace: true
      },
      defaultSortKey: '',
      defaultSortOrder: 1,
      sortKey_1: '',    // ex. 'kana'
      sortOrder_1: 1,
      sortKey_2: '',    // ex. 'era'
      sortOrder_2: 1,
      mapOptions: {
        type: typeof google === 'undefined' ? 'openlayer' : 'googlemap',
        outer_frame_selectors: ['#header', '#footer'],
        container_selector: "#main",
        gmap_selector: '#gmap',
        showBaseMarker: true, // 基準位置マーカー表示
        gmap_options: {},
        gmap_options_default: typeof google === 'undefined' ? {} : {
          zoom: 15, // 初期ズームレベル
          minZoom: 13,
          maxZoom: 19,
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.LEFT_TOP
          },
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          // mapTypeId: google.maps.MapTypeId.TERRAIN,
          // mapTypeId: google.maps.MapTypeId.SATELLITE,
          mapTypeControl: true,
          mapTypeControlOptions: {
            position: google.maps.ControlPosition.TOP_LEFT
          },
          streetViewControl: true,
          streetViewControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM
          },
          panControl: false,
          fullscreenControl: false
          // disableDefaultUI: true, // 全てのパネル（拡大，ストリートビュー）を非表示にする
        },
        olmap_options: {
          selector: 'gmap',
          zoom: 15, // 初期ズームレベル
          minZoom: 13,
          maxZoom: 19,
          rotation: 0,
          tilingMapPath: '',
          useOnlineOSM: true,
          control: {
            scaleLine: true,
            zoomSlider: true
          }
        },
        /*
         Markerを使う場合 display: true
         　※ GoogleMapsの場合、Markerを使うと、地図と吹き出しの間に中心点が描けるようになる。しかし動くと少しガタガタする
         　　CSSで描く場合は、地図と吹き出しの間に描くことはできない。しかし、ガタガタ動かない
         Todo: CSSでは描画できない
         */
        center_cross: {
          display: false,
          using_overlay: true
        },
        // 画面サイズによって、マップの描画域(パネルの背後まで)を可変にするかどうか
        adjustView: {
          enable: true,
          viewSize: 1024, // パネル背後のマップ描画を止める最低画面サイズ
          panelSize: 390  // パネルのサイズ
        },
        setMarkerMargin: false,
        marginWeight: 0.0002,
        useDefaultMarkerLabel: true
      },
      /*
       初期検索の有効化
       ・URLパラメタによる検索優先
       */
      initialSearch: true,
      
      // 検索メニューを描画するか
      renderSearchMenu: true,
  
      keywordSearchMode: false,
      searchWord: '',
  
      defaultView: 'map',
      defaultZoom: 11,
      isNotMapShownOnce: false,
  
      isProduction: false
    };
    
  }
  getService(name){
    if (!this.services[name]){
      this.services[name] = this.serviceProvider.get(name);
    }
    return this.services[name];
  }
  /**
   * モジュールを読み込む
   * ・IHのprototypeを上書きする（後者優先のオーバーライド。coreすらも上書きする）
   * ・各モジュールの extended メソッドを呼ぶ
   * 
   * @returns {undefined}
   */
  extendModules() {
    var that = this;
    $.each(this.modules, function (i, module) {
      if (typeof module === 'string') {
        if (window[module]) {
          module = window[module];
        } else {
          console.warn('undefined module:' + module);
          return;
        }
      }

      const targetObj = (typeof(module) === "function")
        ? module.prototype
        : module;

      for (const key of Reflect.ownKeys(targetObj)) {
        if (key === "constructor") continue;

        const descriptor = Object.getOwnPropertyDescriptor(targetObj, key);

        descriptor.configurable = true;
        descriptor.enumerable = false;
        if (descriptor.hasOwnProperty("writable")) {
          descriptor.writable = true;
        }

        Object.defineProperty(IH.prototype, key, descriptor);
      }
      if (module.extended) {
        module.extended(that);
      }
    });
  }
  
  /**
   * 
   * @access public
   * @param {Object} options
   * @returns void
   */
  setOptions(options){
    options = options || {};

    $.extend(true, this, options);
    
    if (options.polygonTypes) {
      this.setOptionsForPolygons(options.polygonTypes);
    }
    if (options.placeTypes) {
      this.setOptionsForPlaceTypes(options.placeTypes);
    }

  }
  setOptionsForPolygons(polygonTypes) {
    // optionsから初期データの設定
    if (polygonTypes.landslide) {
      this.landslide_bool = {};
      this.landslide_filename_table = [];
      this.flag_display_landslide = [];

      for (var id in polygonTypes.landslide) {
        var d = polygonTypes.landslide[id];
        this.landslide_filename_table.push(id);
        this.flag_display_landslide.push(false);
        this.landslide_bool[id] = 0;
      }
    }
    if (polygonTypes.river) {
      this.river_bool = {};
      this.river_name_table = {};
      for (var id in polygonTypes.river) {
        var d = polygonTypes.river[id];
        this.river_bool[id] = 0;
        this.river_name_table[id] = d.name;
      }
    }
  }
  setOptionsForPlaceTypes(placeTypes) {
    this.flag_display_facility = [];
    for (var i = 0; i < placeTypes.length; i++) {
      var place = placeTypes[i];

      this.flag_display_facility.push(place.checked);
    }
    // placeTypeは他のオブジェクトにも共有しているので、差し替えできるように専用のメソッド経由で破壊的に行う
    if (this.placeType) {
      this.placeType.reset(placeTypes, {
        icon_default: this.icon_default,
        icon_file_path: this.icon_file_path
      });
    } else {
      this.placeType = new PlaceType(placeTypes, {
        icon_default: this.icon_default,
        icon_file_path: this.icon_file_path
      });
    }
  }
  /**
   * 
   * @access  public
   * @returns {Promise}
   */
  reset(){
    this.mapEvents = new EventEmitter();
    this.mapEvents.on('ihClickMarker', (place, marker, popup) => {
      $(this).trigger('ihClickMarker', [place, marker, popup]);
    });
    this.mapEvents.on('ihFillMarkerPopup', (place, popup) => {
      $(this).trigger('ihFillMarkerPopup', [place, popup]);
    });
    this.mapEvents.on('ihChangeZoom', zoom => {
      $(this).trigger('ihChangeZoom', zoom);
    });
    this.popupFactory = new PopupFactory({
      place_image_path: this.place_image_path,
      placeType: this.placeType,
      mapEvents: this.mapEvents,
      streetviewAPIKey: this.options.googleMapsAPIKey
    });

    this.repositoryEvents = new EventEmitter();
    this.repositoryEvents.on('ihBeforeSearch', (request) => {
      $(this).trigger('ihBeforeSearch', [request]);
    });
    this.repositoryEvents.on('ihAfterSearch', (request, response) => {
      $(this).trigger('ihAfterSearch', [request, response]);
    });
    
    this.repository = this.createRepository(this.repositoryOptions);
    this.repository.setEvents(this.repositoryEvents);
  
    this.map = this.createMap(this.mapOptions);
    this.map.reset();
    
    if (this.options.show_address) {
      this.geocoder = this.createGeocoder();
    }
  
    // ビューポートサイズ調整
    // OpenLayersの場合は地図描画( map.start() )前に実行しないと描画が崩れるためここで実行
    this.adjustMapSize();
    $(window).bind("resize", (event) => {
      this.adjustMapSize(event);
    });

    return Promise.resolve();
  }
  createGeocoder() {
    if (typeof google !== 'undefined') {
      return new google.maps.Geocoder();
    } else {
      return null;
    } 
  }
  /**
   * 
   * @param options
   * @returns {Map}
   */
  createMap(options) {
    if (options.type === 'googlemap') {
      return new GoogleMap(this.mapEvents, this.popupFactory, this.placeType, options);
    } else if (options.type === 'openlayer') {
      return new OpenLayerMap(this.mapEvents, this.popupFactory, this.placeType, options);
    }
    throw new Error('unknown map: ' + options.type);
  }
  /**
   * 
   * @param options
   * @returns {Repository}
   */
  createRepository(options) {
    if (options.type === 'builtin') {
      return new BuiltinRepository(options);
    } else if (options.type === 'facet') {
      return new FacetRepository(options);
    }
    throw new Error('unknown repository: ' + options.type);
  }
  /**
   * 初期位置を取得
   * 
   * @returns {google.maps.LatLng}
   */
  getInitLocation(){
    return this.map.createLatLng(this.init_location.lat, this.init_location.lng);
  }
  /**
   * @access  public
   * @returns void
   * 
   * ハザードマップを開始する。HTMLのロード後に実行すること
   * @return Promise
   */
  start() {
    return Promise.resolve()
      .then(() => this.reset())
      .then(() => this.beforeStart())
      .then(() => this._start())
      .then(() => this.afterStart());
  }
  /**
   * モジュール上書き用
   * 
   * @protected
   * @return Promise
   */
  beforeStart() {
    return Promise.resolve();
  }
  /**
   * モジュール上書き用
   *
   * @protected
   * @return Promise
   */
  afterStart() {
    return Promise.resolve();
  }
  /**
   *
   * @private
   * @return Promise
   */
  _start() {
    
    this.map.start();

    var that = this;

    // 既定のハンドラー（ここはモジュール読み込み後に実行されるので最後になるのかも？）
    $(this).on('ihClickMarker', (event, place, marker, popup) => {
      this.clickMarkerHandler(event, place, marker, popup);
    });
    $(this).on('ihClickMarker', function(event, place, marker, popup){
      that.lastClickedPlace = place;
    });

    $(this).on('ihAfterRequestPlaces', function(event, request, places){
      that.displaySearchResult(places);
      that.map.fitBounds(places, this.fit_bounds);
      that.map.displayMarkers(request, places);
      this.displayLoading(false);
      if (this.map.markerMargin.set) {
        this.map.markerMargin.map = {};
        this.map.markerMargin.index = {};
      }
    });
    $(this).on('ihBeforeRequestPlaces', function(event, request){
      that.clear();
      this.displayLoading(true);
      if (this.map.markerMargin.set) {
        this.map.markerMargin.map = {};
        this.map.markerMargin.index = {};
      }
    });

    $(this).on('ihReady', function(event){
      that.ready();
    });
    $('.sr-next').on('click', function(){
      that.showNextSearchResult();
    });
    
    /**
     * @event IH.ready
     */
    $(this).trigger('ihReady');
    $(this).trigger('ready'); // backward compatibility

    // ready直後に動作
    // show_*等実行させるため、次のターンで

    // TODO setloc_placeは_searchのあとで基準位置を更新してるので、確実にその後に実行できるようにしたい
    // firstActionを配列でasync.seriesで実行できれば...
    setTimeout(function(){
      that.firstAction();
    }, 1000);
    
    return Promise.resolve();
    
  }
  ready(){
    var that = this;
    
    this.map.setCenterAndCurrentLocation(this.getInitLocation());
  
    let qs = this.getQueryString();
    let hideCenterMark = false;
    let hidePlaceName = false;
    
    // URLパラメタによる十字マーカ/地名表示の指定
    if (qs.hide) {
      
      qs = qs.hide.split(',');
  
      hideCenterMark = (qs[0] === 'center_mark');
      hidePlaceName = (qs[0] === 'place_name');
      
      if (qs[1] && !hideCenterMark) {
        hideCenterMark = (qs[1] === 'center_mark');
      }
  
      if (qs[1] && !hidePlaceName) {
        hidePlaceName = (qs[1] === 'place_name');
      }
      
    }
    
    // 十字マーカ表示
    if (this.mapOptions.center_cross.display && !hideCenterMark) {
      this.map.displayCenterCross();
    }
    
    // 地名表示 (GoogleMaps固有？)
    if(this.mapOptions.type === 'googlemap'){
      if (!this.options.show_map_label || hidePlaceName) {
        this.map.setStyledMap();
      }
    }
    
    this.controlViewPort();
  }
  
  firstAction () {
    
    let that = this;
  
    // URLパラメタの取得
    let qs = this.getQueryString();
    
    // データIDによる単一データの検索（※placeIdではなく登録データの「ID」ファセット値）
    if (qs.id && qs.id !== '') {
      this.initialSearch = false;
      this.searchById(qs.id);
    }
    
    // URLパラメタによるおすすめコースの表示
    // URLパラメタによる検索を行う場合は、初期選択は行わない
    if (qs.show_course && qs.show_course !== '') {
  
      this.initialSearch = false;
      let id = parseInt(qs.show_course, 10);
      
      that.openCourse(id);
      
      $('#page-list').panel('open');
  
    // URLパラメタによる指定の施設タイプに該当する施設の表示
    } else if (qs.show_placeType && qs.show_placeType !== '') {
  
      this.initialSearch = false;
      let placeTypeIds = qs.show_placeType.split(',');
      
      for (let pid of placeTypeIds) {
        this.flag_display_facility[pid] = true;
      }
      
      this.searchFromBaseMarker();
    
    // URLパラメタによる施設の表示
    } else if (qs.show_place && qs.show_place !== '') {
  
      this.initialSearch = false;
      let placeIds = qs.show_place.split(',');
      let pid = parseInt(placeIds[0], 10);
      let req = {places: placeIds};
  
      that.requestPlaces(req).then(function (res) {
  
        // (1) 表示位置調整
        // 　・fitBoundsが無効の場合、地図中心を指定した施設に合わせる
        // 　・指定した施設が複数の場合、１つめに指定した施設に合わせる
        // (2) 情報ウィンドウと詳細パネルを開く
        // 　・指定した施設が1つの場合のみ
        // ※ (1)(2)とも、show_*以外で、初期表示される施設は対象外
        $(that.map).on('ihDisplayedAllMarkers', function (event, places, marker, popup) {
          
          if ( placeIds && pid === places[0].id ) {
            
            if (!that.fit_bounds) {
              let latlng = that.map.createLatLng(res.places[0].lat, res.places[0].lng);
              that.map.setCenter(latlng);
            }
  
            if (placeIds.length === 1) {
              marker = that.map.getPlaceMarker(pid);
              google.maps.event.trigger(marker, 'click');
            }
      
            placeIds = null;
      
          }
    
        });

        that.controller.clearAllSelector();
        
      });
      
    }
    
    // URLパラメタによる所在地マーカ初期値の設定 (施設ID)
    if (qs.setloc_place && qs.setloc_place !== '') {
      
      let req = {places: [qs.setloc_place], origin: 'system'};
      
      this._search(new Request(req)).then(function(res){
        if (res.places && res.places.length > 0){
          let latlng = that.map.createLatLng(res.places[0].lat, res.places[0].lng);
          that.map.setCenter(latlng);
          that.map.updateCurrentLocation(latlng);
        }
      });
      
    // URLパラメタによる所在地マーカ初期値の設定 (座標)
    } else if (qs.setloc_lat && qs.setloc_lat !== '' && qs.setloc_lng && qs.setloc_lng !== '') {
      
      let lat = qs.setloc_lat;
      let lng = qs.setloc_lng;
    
      if (lat && lng){
        lat = lat.replace("#", "");
        lng = lng.replace("#", "");
      
        if (parseInt(lat, 10) > 30 && parseInt(lng, 10) > 130) {
          let latlng = this.map.createLatLng(lat, lng);
          that.map.setCenter(latlng);
          that.map.updateCurrentLocation(latlng);
        }
      }
    
    }
    
    // URLパラメタによるGPS取得位置の設定
    if (qs.gps_lat && qs.gps_lat !== '' && qs.gps_lng && qs.gps_lng !== '') {
      that.setGeolocationMock({lat: qs.gps_lat, lng: qs.gps_lng});
    }
    
    // 初期選択値による検索
    // 初期選択値がなければ、検索パネル表示のみ
    if (this.initialSearch) {
      this.searchFromCenter();
      $('#page-search').panel('open');
    }
    
  }
  
  /**
   * 既定のマーカークリック動作
   * 
   * @access protected
   * @param event
   * @param place
   * @param marker
   * @param popup
   */
  // clickMarkerHandler(event, place, marker, popup) {
  //   this.map.toggleMarkerPopup(marker, popup);
  // }
  /**
   * v3.3から、高低差表示すると、選択したマーカーとパネルの情報があわなくなってしまった。そのため、常に選択したマーカーとパネルは同期するように変更
   *
   * v3.2までのフロー
   *
   * 0. 表示なし
   *  |
   *  | ＜マーカークリック or パネル等のクリック＞
   *  v
   * 1. マーカー表示
   *  |
   *  |------------＜マーカークリック＞---> 0.へ【未実装: パネル等のクリックは2になるようにしたい】
   *  |
   *  | ＜マーカー or ポップアップクリック or パネル等のクリック＞
   *  v
   * 2. マーカー表示 + パネル表示
   *
   * @param {type} event
   * @param {type} place
   * @param {type} marker
   * @param {type} popup
   */
  clickMarkerHandler(event, place, marker, popup) {

    if (this.map.forceMarkerOpen || !this.map.isOpenMarkerPopup(popup)) {
      if (this.show_detail_panel) {
        this.openInfoPanel(place);
      }
    }

    this.map.toggleMarkerPopup(marker, popup);

    $(popup).on('click', () => {
      this.openInfoPanel(place);
    });

  }
  /**
   * パネルを開き、施設の詳細を表示する
   *
   * @param {type} place
   * @returns {undefined}
   */
  openInfoPanel(place) {
    var latlng = this.map.createLatLng(place.lat, place.lng);
    // this.map.setCenter(latlng);
    
    // TODO: セレクタはオプションにするか拡張でやりたい。Controllerでやるべき？
    $('#page-place').panel('open');
    $('#page-place').data('place-id', place.id); // for tracking
    this.popupFactory.fillMarkerPopup(place, $('#page-place'));
  }
  closeInfoPanel(place) {
    // TODO: セレクタはオプションにするか拡張でやりたい。Controllerでやるべき？
    // パネルを閉じる
    $('.ui-panel').panel('close');
  }
  /**
   * util
   * URLのQueryStringを取得し，連想配列で返す
   * 
   * @returns {Object}
   */
  getQueryString() {
    /**
     *
     * http://phpspot.org/blog/archives/2009/09/jqueryquerystri.html
     */
    var vars = {}, hash;
    var hashes = window.location.href.slice(window.location.href.indexOf('?') + 1).split('&');
    for (var i = 0; i < hashes.length; i++) {
      hash = hashes[i].split('=');
//      vars.push(hash[0]);
      vars[hash[0]] = hash[1];
    }
    return vars;
  }
  
  /**
   * 座標から住所を取得する
   * 
   * @uses geocoder
   * 
   * @param {type} latlng
   * @param {type} callback
   * @returns {undefined}
   */
  getAddress(latlng, callback) {
    if (this.geocoder) {
      this.geocoder.geocode({'latLng': latlng, 'language': 'ja'}, function(results, status) {
        if (status == google.maps.GeocoderStatus.OK) {
          if (results[1]) {
            callback(results[1].formatted_address);
          }
        }
      });
    }
  }
  
  displayLoading(bool_loading) {
    if (bool_loading){
      $.mobile.loading( 'show', {
          textVisible: false,
          theme: "a",
          textonly: false
      });
    }else{
      $.mobile.loading( "hide" );
    }
  }

  getSearchTargetTypes() {
    /**
     * 検索対象となっている施設の番号を得る
     * 形式：ハイフン区切り（ex. 1-2-3）
     */
    var types = [];

    for (var i = 0, len = this.flag_display_facility.length; i <= len; i++) {
      if (this.flag_display_facility[i]) {
        types.push(i);
      }
    }
    
    return types;
  }
  /**
   * 検索等で変更された状態を初期状態に戻す
   * 
   * @returns {undefined}
   */
  clear(){
    this.map.clearMarkersAll();
    this.clearSearchResult();
    this.map.displayCircle(0);
    $(this).trigger('ihClear');
  }
  
  
  static fillUrls(urls, tpl){
    tpl.hide();
    var parent = tpl.parent();
    parent.find('.real-link').remove();
    
    for (var i = 0; i < urls.length; i++){
      parent.append(
        tpl.clone().attr('href', urls[i].url).text(urls[i].label).addClass('real-link').show()
      );
      parent.append(document.createTextNode(' '));
    }
  }
  static parseUrl(str){
    var ret = [];
    var list = str.split(/\s*\|\s*/);
    var m, index;
    for (var i = 0; i < list.length; i++){
      m = list[i].match(/https?:\/\/.*/);
      if (!m){
        continue;
      }
      index = list[i].indexOf(m);
      if (index === 0){
        ret.push({label: m[0], url: m[0]});
      }else{
        ret.push({label: list[i].substr(0, index).trim(), url: m[0]});
      }
    }
    return ret;
  }
  getLastClickedPlace(){
    return this.lastClickedPlace;
  }
  clearSearchResult(){
    let tpl = $('.sr-template');
    let container = $("#ih-collapsed-result-container");
    container.empty();
    container.append(tpl);
    $('#ih-expanded-result-container').empty();
    $('#ih-tiled-result-container').empty();
  }
  /**
   * 検索結果リストを表示
   * 
   * @param {type} json
   * @returns void
   */
  displaySearchResult(places) {
    /*
     * 検索結果リストを表示
     */
    this.clearSearchResult();
    this.searchResultOffset = 0;
    this.searchResultPlaces = places;
    
    var that = this;

    if (places.length === 0){
      $('.sr-notfound').show();
      $('.sr-next').hide();
      return;
    }
    $('.sr-notfound').hide();
    
    if (places.length > this.searchResultLimit){
      $('.sr-next').show();
    }else{
      $('.sr-next').hide();
    }

    var placesRanged = places.slice(this.searchResultOffset, this.searchResultLimit);

    $.each(placesRanged, function(i, place){
      if (!place) return;
      that.displaySearchResultRow(place);
    });
  }
  showNextSearchResult(){
    var that = this;
    var places = that.searchResultPlaces;
    var offset = that.searchResultOffset = that.searchResultOffset + that.searchResultLimit;
    var limit = that.searchResultLimit;

    if (places.length >= offset + limit){
      $('.sr-next').show();
    }else{
      $('.sr-next').hide();
    }

    var placesRanged = places.slice(offset, offset + limit);
    $.each(placesRanged, function(i, place){
      if (!place) return;
      that.displaySearchResultRow(place);
    });
  
    // タイルのマージン調整用のダミータイル挿入
    $('.ih-empty-tile').remove();
    let container = $('#ih-tiled-result-container');
    for (let i = 0; i < 20; i++) {
      container.append($('<div>', { class: 'ih-expanded-result-tile ih-empty-tile' }));
    }
    
    this.adjustTileMargin();
  }
  displaySearchResultRow(place){
    let that = this;
    let containers = $(".sr-container");
    let tpl = $('.sr-template');
    let t;
    let image = "";
    
    if (place.image) {
      image = place.image.split('|')[0].replace(/[\n\r\s]/g, '');
      if (!image.match(/^http/)) {
        image = this.place_image_path + image;
      }
    }
    
    // サイドパネル版とビューポート版の検索結果一覧をそれぞれ生成する
    containers.each( function () {
      let containerId = $(this).attr('id');
      
      if ( containerId === 'ih-collapsed-result-container' ) {
        t = tpl.clone().removeClass('sr-template').show();
      } else if ( containerId === 'ih-tiled-result-container' ) {
        if (image === '') {
          image = 'img/no_image.png'
        }
        t = $('<div class="ih-expanded-result-tile">' +
                '<a data-role="button" class="sr-link ih-tile">' +
                  '<img src ="' + image + '" class="ih-expanded-result-item-thumbnail"><br>' +
                  '<span class="sr-name"></span>' +
                '</a>' +
              '</div>');
      }
  
      // 施設選択状態の見た目を変更するなどに使用する
      t.addClass('place-id-' + place.id);
      t.data('place-id', place.id);
      
      if ('icon_min' in place) {
        t.find('.sr-img').attr('src', place.icon_min);
      } else {
        t.find('.sr-img').attr('src', that.placeType.getTypeIcon(place.type));
      }
      t.find('.sr-name').text(place.name);
      t.find('.sr-type').text('（' + that.placeType.getName(place.type) + '）');
      t.find('.sr-link').on('click', function(){
        that.map.forceMarkerOpen = true;
        t.siblings().removeClass('js-selected');
        t.addClass('js-selected');
        let latlng = that.map.createLatLng(place.lat, place.lng);
        that.map.setCenter(latlng);
        that.map.focusMarker(place);
      });
      if (typeof place.distance !== 'undefined'){
        t.find('.sr-distance').text(Math.ceil(place.distance.toFixed(2) * 1000) + "m");
      }
      
      $(that).trigger('ihFillSearchResult', [place, t]);
      
      if ( containerId === 'ih-collapsed-result-container' ) {
        $(this).append(t);
      } else if ( containerId === 'ih-tiled-result-container' ) {
        $(this).append(t);
        $('.ih-tile').each(function () {
          $(this).button();
        });
      }
    });
  }
  // filter
  // 元のデータに空を意味する "-" が含まれるので除去
  placeDataFilter(place){
    for (var k in place){
      if (typeof place[k] === 'string'){
        place[k] = place[k].replace(/^-$/, '');
      }
    }
    return place;
  }
  /**
   * 施設やポリゴンデータを検索する
   * 
   * @param {Request} request
   * @returns {jQuery.Deferred}
   */
  _search(request) {
    
    return this.repository.search(request);
    
  }
  
  /**
   *
   * @param request
   * @param response
   * @returns {*}
   */
  transformPlacesResponse(request, response) {
    var that = this;
    if (request.get('order')){
      var placeIds = request.get('order');
      // resは順不同なのでリクエストしたplacesIdの順序に並べ替える
      var places = [];
      var map = {};
      _.each(response.places, function(place, k){
        map[place.id] = place;
      })
      _.each(placeIds, function(v, k){
        places.push(that.placeDataFilter(map[v]));
      });
      response.places = places;
    }else{
      that.map.displayCircle(that.getRadius());
      if (that.arrangePlaces){
        response.places = that.arrangePlaces(response.places);
      }
    }
  
    response.places = _.filter(response.places, function(place){
      return !!place;
    });
    $(that).trigger('ihAfterTransformPlacesResponse', [request, response.places]);
    
    return response;
  }
  /**
   * 座標位置や特定の施設IDなどから、検索を実行し、結果を返す
   * 
   * @param {type} params
   * @return {jQuery.Deferred}
   */
  requestPlaces(params){
    var that = this;
    if (this.updateBaseMarker && params.lat && params.lng){
      this.map.updateCurrentLocation(params.lat, params.lng);
    }
    
    if (!params.origin){
      params.origin = 'user';
    }
    if (!params.latlng){
      params.latlng = this.map.getCurrentSearchLocation();
    }
    
    var request = new Request(params);
    this.lastRequest = request;
    $(that).trigger('ihBeforeRequestPlaces', [request]);
    
    return this._search(request).then(function(res){
      res = that.transformPlacesResponse(request, res);
      $(that).trigger('ihAfterRequestPlaces', [request, res.places]);
      return res;
    }).catch(function(err) {
      console.warn(err);
      that.displayLoading(false);
      // that.map.displayCircle(that.getRadius());
    });
  }
  /**
   * 前回の検索設定で再検索する
   * 
   */
  reSearch(params) {
    if (!this.lastRequest){
      return;
    }
    var that = this;
    var request = this.lastRequest.extend(params);
    
    $(that).trigger('ihBeforeRequestPlaces', [request]);
    
    return this._search(request).then(function(res){
      
      res = that.transformPlacesResponse(request, res);
      
      // lastClickedPlaceの情報を更新（距離が変わる）
      var lp = that.getLastClickedPlace();
      _.each(res.places, function(place){
        if (place && lp && place.id === lp.id){
          that.lastClickedPlace = place;
        }
      });

      $(that).trigger('ihAfterRequestPlaces', [request, res.places]);
      return res;
    }).catch(function() {
      that.displayLoading(false);
      that.map.displayCircle(that.getRadius());
    });
  }
  /**
   * 
   * @returns {jQuery.Deferred}
   */
  searchFromCenter() {
    /**
     * 地図の中心点から検索する
     */
    var c = this.map.getCenter();

    this.search_method_prev_num = 0;

    // マーカーの読み込み
    return this.requestPlaces({
      lat: c.lat(),
      lng: c.lng(),
      radius: this.getRadius(),
      types: this.getSearchTargetTypes()
    });
  }
  /**
   * 基準位置を中心に検索
   * @returns {unresolved}
   */
  searchFromBaseMarker(){
    var c = this.map.getCurrentSearchLocation();
    
    return this.requestPlaces({
      lat: c.lat(),
      lng: c.lng(),
      radius: this.getRadius(),
      types: this.getSearchTargetTypes()
    });
  }
  /**
   * 半径を変更
   *
   * @param {String} radius
   * @returns {String}
   */
  setRadius(radius) {
    this.selected_radius = radius; // radiusは文字列型
    return radius;
  }
  getRadius(){
    return this.selected_radius;
  }
  
  controlViewPort() {
    const tileViewBtn = $('.hz-tile-view-button');
    const mapViewBtn = $('.hz-map-view-button');
    
    if (this.defaultView === 'tile') {
      this.showTileView();
    }  else {
      this.showMapView();
    }
    tileViewBtn.on('click', () => {
      this.showTileView();
    });
    mapViewBtn.on('click', () => {
      this.showMapView();
    });
  
    // ウィンドウリサイズ時に拡大版検索結果表示エリアの高さ調整
    $(window).bind("resize", event =>  {
      this.adjustExpandedResultHeight();
    });

    // タイルのマージン調整用のダミータイル挿入
    $(this.map).on('ihDisplayedAllMarkers', () => {
      const container = $('#ih-tiled-result-container');
      for (let i = 0; i < 20; i++) {
        container.append($('<div>', { class: 'ih-expanded-result-tile ih-empty-tile' }));
      }
      this.adjustTileMargin();
    });
  }
  
  showMapView() {
    $('#gmap').show();
    $('#ih-expanded-result-viewport').hide();
    // デフォルトズームに設定する
    this.map.setZoom(this.defaultZoom);
    // デフォルト中心位置が指定されている場合は中心位置を調整する
    if (this.defaultMapCenter) {
      const center = this.map.createLatLng(this.defaultMapCenter.lat, this.defaultMapCenter.lng);
      this.map.setCenter(center);
    }
  }
  showTileView() {
    $('#ih-expanded-result-viewport').show();
    $('#gmap').hide();
    this.adjustExpandedResultHeight();
  }
  
  /**
   * ビューポートに拡大した結果一覧の高さを調整
   */
  adjustExpandedResultHeight(){
    let viewport = $('#ih-expanded-result-viewport');
    let controllerHeight = viewport.find('.ih-list-controller').height();
    if ( controllerHeight > 0 ) {
      let viewportHeight = viewport.height();
      let expandedResultContent = $('.ih-expanded-result-content');
      expandedResultContent.height( viewportHeight - controllerHeight );
    }
  }

  /**
   * ビューポートのサイズ調整
   *
   */
  adjustMapSize() {
    const minus = _.reduce(this.mapOptions.outer_frame_selectors, function (val, frame) {
      return val + $(frame).height() + 0;
    }, 0);
    const height = window.innerHeight - minus; // iPhone safari: height()だとアドレスバーの部分が引かれた値になる
    const width = $(window).width();
    const container = $(this.mapOptions.container_selector);
    const mapViewport = $(this.mapOptions.gmap_selector);
  
    container.height(height);
  
    // ビューポートの横幅が1024px(初期設定)を越える時、横幅をサイドパネル分(初期設定 390px)縮める
    let side = 0;
    if (this.mapOptions.adjustView.enable && width >= this.mapOptions.adjustView.viewSize) {
      side = this.mapOptions.adjustView.panelSize;
    }
  
    mapViewport.width(width - side);
    mapViewport.height(height);
  
    // 結果一覧をビューポートに拡大して描画するためのサイズ調整
    let resultViewport = $('#ih-expanded-result-viewport');
    resultViewport.width(width - side - 20);
  }
  
  adjustTileMargin () {
    // 画像一覧右側の余分なマージンをタイル内マージンに分配
    if ($('#ih-expanded-result-viewport').width() < 360) {
      $('.ih-expanded-result-tile').removeClass('ih-flex-grow');
    } else {
      $('.ih-expanded-result-tile').addClass('ih-flex-grow');
    }
  
    // [応急処置] Edgeの場合、画像一覧のサムネイルが初期表示で大きく下にずれることがある
    // サムネイル表示後、タイルの高さを調整するとずれを修正できる
    let ua = window.navigator.userAgent.toLowerCase();
    if(ua.indexOf('edge') !== -1) {
      setTimeout( () => {
        const tile = $('.ih-tile');
        const height = tile.height();
        if (height === 140) {
          tile.height(141);
        } else {
          tile.height(140);
        }
      }, 3000)
    }
  }
  
  disableUi (selectors) {
    const len = selectors.length;
    for (let i = 0; i < len; i++) {
      $(selectors[i]).addClass('ui-disabled');
    }
  }
  enableUi (selectors) {
    const len = selectors.length;
    for (let i = 0; i < len; i++) {
      $(selectors[i]).removeClass('ui-disabled');
    }
  }
  
  /**
   * データIDによる単一データの検索（placeIdではなく登録データの「ID」ファセット値）
   *
   * @param id
   */
  searchById (id) {
    let init = true;
    this.isIdSearchMode = true;
    this.controller.clearAllSelector();
    
    // IDで検索
    const facetValueId = this.repository.getFacetValueIdsByName([id])[0];
    this.initSearchTargetTypes();
    this.searchTargetTypes[facetValueId] = true;
    this.searchFromBaseMarker();
  
    // マーカー表示後: 詳細パネルを開く
    $(this.map).on('ihDisplayedAllMarkers', (event, places, marker, popup) => {
      if (this.isIdSearchMode) {
        const place = places[0];
        const latlng = this.map.createLatLng(place.lat, place.lng);
        this.map.setCenter(latlng);
        this.map.focusMarker(place);
        this.isIdSearchMode = false;
      }
    });
  
    // データ件数更新後: 検索ターゲット変数と検索メニューのデータ件数を初期化し、選択解除ボタンを有効化する
    $(this.controller).on('ihAfterUpdateFacetValue', () => {
      if (init) {
        this.initSearchTargetTypes();
        this.initSearchMenu();
        $('#all-clear-button').prop('disabled', false);
        $('#all-clear').find('.ui-btn-inner').css('color', 'black');
        init = false;
      }
    });
  }
  
  initSearchTargetTypes () {
    this.searchTargetTypes = [];
  }
  initSearchMenu () {
    this.controller.displayNumberOfFacetValue(null, false);
  }
  /**
   * ビューポートの表示が地図かどうか
   *
   * @returns {Boolean}
   */
  isMapViewDisplayed () {
    const $mapViewDisplayer = $('.hz-map-view-button');
    return $mapViewDisplayer.prop('checked');
  }
}

module.exports = IH;



// WEBPACK FOOTER //
// ./map/js/ih/z.core.js